import {
  AgentFilter,
  AnalyticsAgent,
  ConversationMessage,
  ConversationsPerDay,
  ConversationsPerWeek,
  FeedbackSummary,
  MessagesPerDay,
  MessagesPerWeek,
  NegativeFeedbackList,
  UsersPerDay,
  WeeklyActiveUsers,
} from '../../types/analyticsTypes'
import { axiosClient } from '../axiosClient'

type QueryValue = string | number | null | undefined

const withQuery = (path: string, params: Record<string, QueryValue>): string => {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      search.set(key, String(value))
    }
  })
  const query = search.toString()
  return query ? `${path}?${query}` : path
}

export const analyticsFunctions = {
  fetchAgents: async (): Promise<AnalyticsAgent[]> => {
    return await axiosClient.get('api/analytics/agents')
  },

  fetchFeedbackSummary: async (
    agentId: AgentFilter,
    days: number | null,
  ): Promise<FeedbackSummary> => {
    return await axiosClient.get(
      withQuery('api/analytics/feedback-summary', { agent_id: agentId, days }),
    )
  },

  fetchWeeklyActiveUsers: async (agentId: AgentFilter): Promise<WeeklyActiveUsers[]> => {
    return await axiosClient.get(
      withQuery('api/analytics/weekly-active-users', { agent_id: agentId }),
    )
  },

  fetchConversationsPerDay: async (agentId: AgentFilter): Promise<ConversationsPerDay[]> => {
    return await axiosClient.get(
      withQuery('api/analytics/conversations-per-day', { agent_id: agentId }),
    )
  },

  fetchConversationsPerWeek: async (
    agentId: AgentFilter,
  ): Promise<ConversationsPerWeek[]> => {
    return await axiosClient.get(
      withQuery('api/analytics/conversations-per-week', { agent_id: agentId }),
    )
  },

  fetchMessagesPerDay: async (agentId: AgentFilter): Promise<MessagesPerDay[]> => {
    return await axiosClient.get(
      withQuery('api/analytics/messages-per-day', { agent_id: agentId }),
    )
  },

  fetchMessagesPerWeek: async (agentId: AgentFilter): Promise<MessagesPerWeek[]> => {
    return await axiosClient.get(
      withQuery('api/analytics/messages-per-week', { agent_id: agentId }),
    )
  },

  fetchUsersPerDay: async (agentId: AgentFilter): Promise<UsersPerDay[]> => {
    return await axiosClient.get(
      withQuery('api/analytics/users-per-day', { agent_id: agentId }),
    )
  },

  fetchNegativeFeedback: async (
    limit: number,
    offset: number,
    days: number,
    agentId: AgentFilter,
  ): Promise<NegativeFeedbackList> => {
    return await axiosClient.get(
      withQuery('api/analytics/negative-feedback', {
        limit,
        offset,
        days,
        agent_id: agentId,
      }),
    )
  },

  exportNegativeFeedback: async (days: number, agentId: AgentFilter): Promise<Blob> => {
    return await axiosClient.get(
      withQuery('api/analytics/negative-feedback/export', { days, agent_id: agentId }),
      { responseType: 'blob' },
    )
  },

  fetchNegativeFeedbackConversation: async (
    messageId: string,
  ): Promise<ConversationMessage[]> => {
    return await axiosClient.get(
      `api/analytics/negative-feedback/${messageId}/conversation`,
    )
  },
}
